// src/pages/Search.js
import React, { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import BASE_URL, { DAJC } from "../config";
import {
  getTitle, getImage, getExcerpt, formatDate, localePath,
} from "../utils/content";
import Pagination from "../components/Pagination";
import Seo from "../components/Seo";

export default function Search() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initial = searchParams.get("keyword") || "";
  const [searchTerm, setSearchTerm] = useState(initial);
  const [debounced, setDebounced] = useState(initial);
  const [filter, setFilter] = useState("all"); // all | article | page
  const [perPage, setPerPage] = useState(9);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const cancelRef = useRef(null);

  // Debounce input
  useEffect(() => {
    const id = setTimeout(() => setDebounced(searchTerm), 400);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // Sync URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (debounced) params.set("keyword", debounced);
    navigate(localePath(lang, `search${params.toString() ? "?" + params.toString() : ""}`), { replace: true });
  }, [debounced, lang, navigate]);

  // Reset to page 1 on filter / term change
  useEffect(() => { setPage(1); }, [debounced, perPage, filter]);

  // Fetch
  useEffect(() => {
    if (!debounced) {
      setResults([]);
      return;
    }
    if (cancelRef.current) cancelRef.current.cancel();
    cancelRef.current = axios.CancelToken.source();
    setLoading(true);

    axios
      .get(`${BASE_URL}/api/search`, {
        params: {
          page,
          per_page: perPage,
          search: debounced,
        },
        cancelToken: cancelRef.current.token,
      })
      .then((r) => {
        const all = r.data?.data || [];
        // Restrict to DAJC scope: only items linked to category 6 or service 5
        const scoped = all.filter((item) => {
          if (!item) return false;
          // If api returns category_id / service_id we filter; otherwise keep all
          if (item.type === "article") {
            if (item.category_id != null) return Number(item.category_id) === DAJC.CATEGORY_ID;
            return true;
          }
          if (item.type === "page") {
            if (item.service_id != null) return Number(item.service_id) === DAJC.SERVICE_ID;
            return true;
          }
          return true;
        });
        const filtered = filter === "all" ? scoped : scoped.filter((x) => x.type === filter);
        setResults(filtered);
        setTotalPages(r.data?.pagination?.last_page || 1);
        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) setLoading(false);
      });
  }, [page, perPage, debounced, filter]);

  return (
    <>
      <Seo path="search" title={debounced ? `"${debounced}"` : t("search.title")} noindex />

      <section className="page-header">
        <div className="container">
          <div className="page-header__inner">
            <div className="page-header__crumbs">
              <Link to={localePath(lang)}>{t("common.home")}</Link>
              <span>/</span>
              <span>{t("search.title")}</span>
            </div>
            <h1>{t("search.title")}</h1>
            {debounced && (
              <p>
                {t("search.resultsFor")} <em style={{ color: "var(--gold-soft)" }}>"{debounced}"</em>
              </p>
            )}
          </div>
        </div>
      </section>

      <section style={{ background: "var(--ivory)" }}>
        <div className="container">
          <div className="filter-bar">
            <div className="filter-bar__search">
              <i className="bi bi-search" />
              <input
                type="text"
                placeholder={t("search.placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} aria-label="Clear">
                  <i className="bi bi-x-lg" />
                </button>
              )}
            </div>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">{t("search.all")}</option>
              <option value="article">{t("search.articles")}</option>
              <option value="page">{t("search.documents")}</option>
            </select>
            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
              <option value={6}>6 / {t("search.perPage")}</option>
              <option value={9}>9 / {t("search.perPage")}</option>
              <option value={12}>12 / {t("search.perPage")}</option>
              <option value={24}>24 / {t("search.perPage")}</option>
            </select>
          </div>

          {!debounced ? (
            <div className="empty">
              <i className="bi bi-search" style={{ fontSize: 64, color: "var(--gold)", opacity: 0.4 }} />
              <h3 style={{ marginTop: 24 }}>{t("search.noKeyword")}</h3>
              <p>{t("search.enterKeyword")}</p>
            </div>
          ) : loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : results.length === 0 ? (
            <div className="empty">
              <i className="bi bi-journal-x" style={{ fontSize: 64, color: "var(--gold)", opacity: 0.4 }} />
              <h3 style={{ marginTop: 24 }}>{t("search.noResults")}</h3>
              <p>{t("search.tryDifferentTerms")}</p>
            </div>
          ) : (
            <div className="cards-grid">
              {results.map((item) => {
                const img = getImage(item, lang);
                const targetType = item.type || "article";
                return (
                  <article key={`${targetType}-${item.id}`} className="card-article">
                    <div className="card-article__media">
                      {img ? (
                        <img src={img} alt={getTitle(item, lang)} loading="lazy" />
                      ) : (
                        <div className="card-article__media-fallback">
                          <i
                            className={`bi ${targetType === "page" ? "bi-file-earmark-text" : "bi-newspaper"}`}
                            style={{ fontSize: 48, color: "var(--gold)" }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="card-article__body">
                      <div className="card-article__date">
                        {targetType === "page"
                          ? t("search.documents")
                          : formatDate(item.created_at, lang, t)}
                      </div>
                      <h3 className="card-article__title">
                        <Link to={localePath(lang, `${targetType}/${item.id}/${item.slug || ""}`)}>
                          {getTitle(item, lang, 80)}
                        </Link>
                      </h3>
                      <p className="card-article__excerpt">{getExcerpt(item, lang, 22)}</p>
                      <Link
                        to={localePath(lang, `${targetType}/${item.id}/${item.slug || ""}`)}
                        className="card-article__more"
                      >
                        {t("articles.read_more")}
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {debounced && results.length > 0 && totalPages > 1 && (
            <Pagination current={page} total={totalPages} onChange={setPage} />
          )}
        </div>
      </section>
    </>
  );
}